import { useEffect, useState } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { usePrevious } from 'Utils/hooks/usePrevious';
import { valueToObject } from 'Utils/UtilityFunctions';
import { actions as flurryActions } from 'Slice/HelperSlice';
import { useDispatch } from 'react-redux';
import { translateSkipAreas } from 'Utils/Constants';
import Empty from '../assets/svgIcons/Empty';
import Edit from '../assets/svgIcons/Edit';
import Cross from '../assets/svgIcons/Cross';
import Tick from '../assets/svgIcons/Tick';
import {
  secondaryFont, wMedium, primaryHeadingColor, boxShadow,
  primaryFont, maxWidth, retina, maxTablet, maxMobile, smMobile,
} from '../../../Utils/utilsStyle';

type Props = {
  title?: string;
  isEditable?: boolean;
  isIcon?: boolean;
  Icon?: object;
  onEdit?: (newTitle?: string) => void
  disableEditForce?: boolean
  onDisableEditForceComplete?: (newTitle?: string) => void
  groupId?: number
} & typeof defaultProps;

const defaultProps = {
  title: '',
  isEditable: true,
  isIcon: true,
  Icon: <Empty />,
  onEdit: () => { },
  disableEditForce: false,
  onDisableEditForceComplete: () => { },
  groupId: 0,
};

const Wrapper = styled.div<{ isIcon: boolean }>`
  display:flex;
  flex-direction: row;
  align-items: center;
  position:relative;
  > svg{
    margin-right:10px;
    width: 25px;
    min-width: 25px;
  }
  .title-style{
    color:${primaryHeadingColor};
    font-family:${secondaryFont};
    font-weight:${wMedium};
    font-size:30px;
    max-width: 600px;
    @media all and (max-width: ${maxWidth}) , ${retina} {
      font-size: 22px;
    }
    @media all and (max-width: ${maxTablet}) {
      max-width: 500px;
      font-size: 18px;
    }
    @media all and (max-width: ${maxMobile}) {
      max-width: 310px;
      font-size: 16px;
    }
    @media all and (max-width: ${smMobile}) {
      max-width: 160px;
    }
  }
  .edit-allow{
    cursor: pointer;
    margin-left:5px;
    svg{
      vertical-align:bottom;
      min-width: 18px;
    }
  }
  .editable{
    position:relative;
    position: absolute;
    width: 600px;
    height: 30px;
    top: -8px;
    left: 40px;
    left: ${(props) => {
    if (props.isIcon) return '40px';
    return '0px';
  }};
  display:none;
  &.isDisplay{
    display:block;
  }
    .editable-inner{
      position: relative;
      width: 530px;
      height: 30px;
      @media all and (max-width: ${maxTablet}) {
        width: 430px;
      }
      @media all and (max-width: ${maxMobile}) {
        width: 320px;
      }
      @media all and (max-width: ${smMobile}) {
        width: 200px;
      }
      input{
        position: absolute;
        background-color:#fff;
        border-radius:8px;
        border:none;
        width: 100%;
        height: 45px;
        top:0;
        left: -10px;
        outline:none;
        padding:0 70px 0 15px;
        font-size:16px;
        box-shadow:${boxShadow};
        color: #424B50;
        font-family: ${primaryFont};
        font-weight: ${wMedium};
        font-size: 18px;
        @media all and (max-width: ${maxWidth}) , ${retina} {
          height: 35px;
          top: 6px;
        }
        &::focus, &:active{
          outline:none;
          border:none;
        }
      }
      .tick{
        cursor: pointer;
        position: absolute;
        right: 20px;
        top:0;
        height: 45px;
        svg{
          max-width: 17px;
          height: 45px;
        }
      }
      .cross{
        cursor: pointer;
        position: absolute;
        right: 50px;
        top:0;
        height: 45px;
        svg{
          max-width: 13px;
          height: 45px;
        }
      }
    }
  }
`;

const Title = function Title(props: Props) {
  const [editable, setEditable] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editing, setEditing] = useState(false);
  const dispatch = useDispatch();
  const host = window.location.href;

  const {
    title,
    isEditable,
    Icon,
    isIcon,
    onEdit,
    disableEditForce,
    onDisableEditForceComplete,
    groupId,
  } = props;

  const prevTitle = usePrevious(title);
  const prevDisableEditForce = usePrevious(disableEditForce);

  const handleEditTitle = () => {
    if (!editing) {
      setEditing(true);
      onEdit(editTitle);
    }
  };

  useEffect(() => {
    if (prevTitle !== title && editable) {
      setEditable(false);
      setEditTitle('');
      setEditing(false);
    }
  }, [title]);

  useEffect(() => {
    if (!prevDisableEditForce && disableEditForce) {
      setEditing(false);
      onDisableEditForceComplete();
    }
  }, [disableEditForce]);

  const handleEditClick = () => {
    setEditable(true);
    setEditTitle(title);
  };

  return (
    <Wrapper isIcon={isIcon} className="title-wrapper-c">
      {Icon}
      {
        !editable ? (
          <>
            <h6 className={`title-style mb-0 text-truncate ${translateSkipAreas}`}>{title}</h6>
            {isEditable ? (
              <span
                className="edit-allow"
                onClick={handleEditClick}
                onKeyPress={handleEditClick}
                role="button"
                aria-label="open"
                tabIndex={0}
              >
                <Edit />
              </span>
            ) : null}
          </>
        ) : null
      }
      <div className={classNames('editable', { isDisplay: editable })}>
        <div className="editable-inner">
          <input
            type="text"
            name="title"
            defaultValue={title}
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            maxLength={40}
          />
          <span
            className="cross"
            onClick={() => {
              if (!editing) {
                setEditable(false);
                setEditTitle('');
              }
            }}
            onKeyPress={() => {
              if (!editing) {
                setEditable(false);
                setEditTitle('');
              }
            }}
            role="button"
            aria-label="close"
            tabIndex={0}
            style={{
              opacity: editing ? 0.5 : 1,
            }}
          >
            <Cross />
          </span>
          <span
            className="tick"
            onClick={() => handleEditTitle()}
            onKeyPress={() => handleEditTitle()}
            role="button"
            aria-label="close"
            tabIndex={0}
            style={{
              opacity: editing ? 0.5 : 1,
            }}
          >
            <Tick />
          </span>
        </div>
      </div>
    </Wrapper>
  );
};

Title.defaultProps = defaultProps;
export default Title;
